import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { get, groupBy, noop, orderBy } from 'lodash';
import memoize from 'memoize-one';
import ExamContent from './ExamContent';

import { useSnackbar } from 'notistack';
import { saveAnswers, clearAnswers } from 'reduxConfig/actions/savedExamAnswers';
import { getSavedExamAnswers } from 'reduxConfig/selectors';
import { getResponseError } from 'utils';

const getOrderedQuestions = memoize(questions => orderBy(questions, x => get(x, 'courseItem.id')));
const getGroupedQuestions = memoize(orderedQuestions => groupBy(orderedQuestions, x => get(x, 'courseItem.id')));

const Exam = ({
  questions,
  inCourseItemMode,
  itemsPerPage,
  examKey,
  liveChecking,
  onSubmit,
  onError,
  onFinally,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const expensiveFilter = useSelector(getSavedExamAnswers);
  const savedExamAnswers = expensiveFilter(examKey) || {};

  // Reminder: this is ONE-indexed:
  const [currentPage, setCurrentPage] = useState(1);
  const [otherExamContentVariables, setOtherExamContentVariables] = useState(); 

  const [submitting, setSubmitting] = useState(false);
  const [correctAnswerCount, setCorrectAnswerCount] = useState(null);

  const [userQA, setAnswers] = useState(savedExamAnswers);
  const [liveCheckingResults, setLiveCheckingResults] = useState({});

  const setAnswer = (k, v) => {
    const newAnswers = { ...userQA, [k]: v };

    // NOTE We are saving to two separate places here: the state here and Redux.
    // The Redux one will be used for loading saved answers (if a key is set),
    // but otherwise the one in state will be used:
    setAnswers(newAnswers);
    if (examKey) {
      dispatch(saveAnswers({ examKey, currentPage, answers: newAnswers }));
    }

    if (liveChecking) {
      setLiveCheckingResults(liveChecking({ questions,currentPage, userQA: newAnswers }));
    }
    console.log('page: '+currentPage);
    console.log('save ans: '+JSON.stringify(savedExamAnswers));
  }

  const submit = async () => {
    setSubmitting(true);

    try {
      const correctAnswerCount = await onSubmit({ questions, userQA });
      setCorrectAnswerCount(correctAnswerCount);
      enqueueSnackbar('Exam submitted successfully', { variant: 'success' });

      if (examKey) {
        dispatch(clearAnswers(examKey));
      }

    } catch(e) {
      if (onError) {
        onError(e);
      } else {
        enqueueSnackbar(`Error submitting exam: ${getResponseError(e)}`, { variant: 'error' });
      }
    } finally {
      onFinally();
      setSubmitting(false);
    }
  }

  useEffect(() => {
    let lastPage, firstQuestionIndex, questionsForPage, subtitle = '';
    
    // TODO Implementation could be faster, freezes briefly
    //
    // Addendum: Making ExamContent just return page numbers, and the lag is gone.
    // So most likely the cause of the lag is rerendering the questions themselves.
    if (inCourseItemMode) {
      // Should already be ordered from server, but might not be in cases of backwards compt:
      const orderedQuestions = getOrderedQuestions(questions);
      const groupedQuestions = getGroupedQuestions(questions);

      const courseItemIds = Object.keys(groupedQuestions);
      const currentCourseItemId = courseItemIds[currentPage - 1];
      
      lastPage = courseItemIds.length;
      questionsForPage = groupedQuestions[currentCourseItemId];
      const firstQuestion = get(questionsForPage, '[0]');
      firstQuestionIndex = orderedQuestions.findIndex(x => get(x, 'id') === firstQuestion.id) || 0;
      subtitle = get(firstQuestion, 'courseItem.name');

    } else {
      lastPage = Math.ceil(questions.length / itemsPerPage);
      firstQuestionIndex = 0 + ((currentPage - 1) * itemsPerPage);
      const lastQuestionIndex = firstQuestionIndex + itemsPerPage;
      questionsForPage = questions.slice(firstQuestionIndex, lastQuestionIndex);
    }

    setOtherExamContentVariables({
      lastPage,
      questions: questionsForPage,
      firstQuestionNumber: firstQuestionIndex + 1,
      subtitle,
    });

    return noop;
  }, [questions, currentPage, inCourseItemMode, itemsPerPage]);

  return (
    <ExamContent
      {...rest}
      initialized={!!otherExamContentVariables}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      setAnswer={setAnswer}
      submit={submit}
      submitting={submitting}
      correctAnswerCount={correctAnswerCount}
      userQA={userQA}
      liveCheckingResults={liveCheckingResults}
      highlightIfCorrectWrong={!!liveChecking}
      totalQuestionsCount={questions.length}
      {...otherExamContentVariables}
    />
  )
}

Exam.propTypes = {
  questions: PropTypes.array.isRequired,
  inCourseItemMode: PropTypes.bool,
  itemsPerPage: PropTypes.number, // only used if inCourseItemMode is false
  examKey: PropTypes.string,
  liveChecking: PropTypes.func,
  onSubmit: PropTypes.func,
  onError: PropTypes.func,
  onFinally: PropTypes.func,
}

Exam.defaultProps = {
  questions: [],
  inCourseItemMode: false,
  itemsPerPage: 5,
  onSubmit: noop,
  onFinally: noop,
}

export default memo(Exam);
